import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Zap, Crown, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GeminiService from '../../services/geminiService';
import useGeminiRequest from '../../hooks/useGeminiRequest';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

const AIChatPage = () => {
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your AI career assistant powered by Google's Gemini. I can help you with resume advice, career guidance, skill development, and interview preparation. How can I assist you today?",
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const messagesEndRef = useRef(null);

  const {
    startRequest,
    cancelRequest,
    isProcessing,
    processingStage,
    error: requestError
  } = useGeminiRequest();

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing]);

  const handleSendMessage = async () => {
    if (!inputText?.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now(),
      text: inputText?.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputText?.trim();
    setInputText('');

    try {
      const result = await startRequest(async () => {
        const { response, updatedHistory } = await GeminiService?.chatWithHistory(
          currentInput,
          chatHistory,
          userProfile?.tier || 'free'
        );
        
        setChatHistory(updatedHistory);
        return response;
      }, [
        { stage: 'Understanding your question...', progress: 20 },
        { stage: 'Processing with AI...', progress: 50 },
        { stage: 'Generating response...', progress: 80 },
        { stage: 'Finalizing answer...', progress: 95 }
      ]);

      const aiMessage = {
        id: Date.now() + 1,
        text: result,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "I apologize, but I encountered an error processing your request. Please try again.",
        sender: 'ai',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      id: 1,
      text: "Hi! I'm your AI career assistant powered by Google's Gemini. I can help you with resume advice, career guidance, skill development, and interview preparation. How can I assist you today?",
      sender: 'ai',
      timestamp: new Date()
    }]);
    setChatHistory([]);
  };

  const currentModel = GeminiService?.getModelForTier(userProfile?.tier || 'free', 'text');
  const isPremiumModel = currentModel?.includes('pro');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Career Assistant</h1>
              <p className="text-gray-600">
                Get personalized career advice and resume guidance powered by Google's Gemini AI
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Model Indicator */}
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-full ${
                isPremiumModel ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {isPremiumModel ? <Crown className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                <span className="text-sm font-medium">
                  {isPremiumModel ? 'Gemini 2.5 Pro' : 'Gemini 2.5 Flash'}
                </span>
              </div>
              
              <Button 
                onClick={clearChat}
                variant="outline"
                size="sm"
              >
                Clear Chat
              </Button>
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Messages Area */}
          <div className="h-96 md:h-[500px] overflow-y-auto p-6 space-y-4">
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-3 max-w-xs md:max-w-2xl ${
                  message?.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message?.sender === 'user' ?'bg-blue-500 text-white' 
                      : message?.isError 
                        ? 'bg-red-500 text-white' :'bg-purple-500 text-white'
                  }`}>
                    {message?.sender === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  
                  {/* Message Bubble */}
                  <div className={`px-4 py-3 rounded-lg ${
                    message?.sender === 'user' ?'bg-blue-500 text-white'
                      : message?.isError
                        ? 'bg-red-100 text-red-800 border border-red-200' :'bg-gray-100 text-gray-800'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{message?.text}</p>
                    <p className={`text-xs mt-2 ${
                      message?.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {message?.timestamp?.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Processing Indicator */}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3 max-w-2xl">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  
                  <div className="px-4 py-3 rounded-lg bg-gray-100">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                      <p className="text-sm text-gray-700">{processingStage}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {requestError && !isProcessing && (
              <div className="flex justify-center">
                <div className="flex items-center space-x-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-sm text-red-700">{requestError}</p>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Ask me about your career, resume, skills, or interview prep... (Powered by ${isPremiumModel ? 'Gemini 2.5 Pro' : 'Gemini 2.5 Flash'})`}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                  disabled={isProcessing}
                />
              </div>
              
              <div className="flex flex-col space-y-2">
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputText?.trim() || isProcessing}
                  className="px-6 py-3"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
                
                {isProcessing && (
                  <Button
                    onClick={cancelRequest}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Tips */}
            <div className="mt-3 text-xs text-gray-500">
              <p>
                <strong>Tips:</strong> Ask about resume improvement, career paths, skill gaps, interview questions, 
                or salary negotiations. Press Enter to send, Shift+Enter for new line.
              </p>
            </div>
          </div>
        </div>

        {/* Upgrade Notice for Free Users */}
        {userProfile?.tier === 'free' && (
          <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Crown className="w-5 h-5 text-purple-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-purple-900">Unlock Premium AI Features</p>
                <p className="text-xs text-purple-700 mt-1">
                  Upgrade to access Gemini 2.5 Pro for more detailed responses, longer conversations, and advanced career insights.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatPage;